/**
 * Departure anchors.
 *
 * A travel group departs from one anchor: a named, located place. The kind says
 * what the place is, which decides how it is offered (a station is a natural
 * meeting point, a residential community is where a household starts). The
 * source says where the record came from, which is what the reference pipeline
 * and the attribution rules read.
 */

export const ANCHOR_KINDS = ['metro', 'hub', 'mall', 'landmark', 'residential', 'custom'] as const

export type AnchorKind = (typeof ANCHOR_KINDS)[number]

export const ANCHOR_SOURCES = ['seed', 'vendor', 'geocode', 'geolocation', 'admin'] as const

export type AnchorSource = (typeof ANCHOR_SOURCES)[number]

/**
 * Kinds that are public places.
 *
 * A group is offered these first, because a departure point is visible to every
 * member of the event and a private address is not a place a group meets by
 * default.
 */
export const PUBLIC_ANCHOR_KINDS: readonly AnchorKind[] = ['metro', 'hub', 'mall', 'landmark']

export function isPublicAnchorKind(kind: AnchorKind): boolean {
  return PUBLIC_ANCHOR_KINDS.includes(kind)
}

/**
 * How close two anchors must be to count as the same place.
 *
 * A lookup that misses the table resolves a new place from a vendor; before it
 * is stored, an anchor within this radius is reused instead, so the same corner
 * does not accumulate one row per member who picked it.
 */
export const ANCHOR_REUSE_RADIUS_METERS = 150

/**
 * Timetable split for last-departure data.
 *
 * Weekend service differs from weekday service on every network this project
 * reads, so the two are stored apart.
 */
/**
 * Anchor kinds a timetable exists for.
 *
 * Last-departure reference data is seeded for exactly these kinds, so anything
 * looking for a bound has to look among them rather than at the nearest place of
 * any kind.
 */
export const TRANSIT_ANCHOR_KINDS = ['metro', 'hub'] as const

export const WEEKDAY_TYPES = ['weekday', 'weekend'] as const

export type WeekdayType = (typeof WEEKDAY_TYPES)[number]

/**
 * Day type of a `YYYY-MM-DD` date, read as a calendar date rather than an instant.
 *
 * An event stores its local start, so the day type has to come from that date
 * part: converting it to UTC first would move a Saturday midnight in Shanghai to
 * Friday, and the wrong timetable would be used.
 */
export function weekdayTypeOfLocalDate(localDate: string): WeekdayType {
  const [year, month, day] = localDate
    .slice(0, 10)
    .split('-')
    .map((part) => Number(part)) as [number, number, number]
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
    return 'weekday'
  }
  const weekday = new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  return weekday === 0 || weekday === 6 ? 'weekend' : 'weekday'
}

/** Reference datasets, each refreshed on its own cadence. */
export const REFERENCE_DATASETS = ['anchors', 'poi_coverage', 'last_departures'] as const

export type ReferenceDatasetKey = (typeof REFERENCE_DATASETS)[number]

export const REFERENCE_DATASET_STATUSES = ['ok', 'refreshing', 'stale', 'error'] as const

export type ReferenceDatasetStatus = (typeof REFERENCE_DATASET_STATUSES)[number]

/**
 * Shape of one standardised reference dataset.
 *
 * Each dataset exposes the same three facts so a refresh loop, an administrator
 * screen and the freshness shown next to a recommendation can all be written
 * once: when it was last filled, whether that fill succeeded, and how many rows
 * it holds.
 */
export interface ReferenceDatasetState {
  key: ReferenceDatasetKey
  /** When the dataset was last filled successfully. */
  lastRefreshedAt: number | null
  /** When a refresh was last attempted, successful or not. */
  lastAttemptAt: number | null
  status: ReferenceDatasetStatus
  lastError: string | null
  recordCount: number
  leaseUntil: number | null
}

/** Whether a dataset is older than its cadence and should be refreshed. */
export function isDatasetStale(
  state: Pick<ReferenceDatasetState, 'lastRefreshedAt'>,
  cadenceSeconds: number,
  now: number,
): boolean {
  if (state.lastRefreshedAt === null) {
    return true
  }
  return now - state.lastRefreshedAt >= cadenceSeconds
}

/**
 * How long a failed dataset is left alone before it is attempted again.
 *
 * Without this, a deployment with a rejected key would start a refresh on every
 * round for a month: the failure keeps the dataset stale, and staleness is what
 * triggers the attempt.
 */
export const REFRESH_RETRY_BACKOFF_SECONDS = 15 * 60

/**
 * Whether a refresh should be attempted now.
 *
 * Staleness decides that a refresh is *wanted*; the attempt time decides whether
 * it is *due*, so a dataset that keeps failing is retried on a backoff instead
 * of on every request.
 */
export function shouldRefreshDataset(
  state: Pick<ReferenceDatasetState, 'lastRefreshedAt' | 'lastAttemptAt' | 'status'>,
  cadenceSeconds: number,
  now: number,
  retryBackoffSeconds = REFRESH_RETRY_BACKOFF_SECONDS,
): boolean {
  if (!isDatasetStale(state, cadenceSeconds, now)) {
    return false
  }
  if (state.status === 'error' && state.lastAttemptAt !== null) {
    return now - state.lastAttemptAt >= retryBackoffSeconds
  }
  return true
}
